import {
  type CheckpointState,
  type CheckpointStatus,
  checkpointState,
  nextOpenCheckpoint,
} from '@ofmos/shared/checklist';
import type { CheckpointSettings } from '@ofmos/shared/field-types';
import type { FulfillmentConfiguration } from '@ofmos/shared/fulfillment-configuration';
import type { FieldMetadata, FieldValue } from '../../../data/client';
import { deadlineSentence, toCheckpointField } from '../../record/checklist';
import { FULFILLMENT } from '../shared/configuration';

/**
 * THE ONBOARDING BOARD, AS PURE FUNCTIONS (fulfillment wave, P4; concept 4.3, COMMON section 4).
 *
 * `@ofmos/shared/checklist` owns every deadline: which of the four states a step is in, how
 * many days early or late, which open step comes next. `record/checklist.ts` owns how a state
 * is read out ("Due today", "2 days late", "Not yet possible"). This file only composes them
 * into the onboarding board - who is on it, in which order, what the second line says, how the
 * segments count - and it does no deadline arithmetic of its own, so a row here and a colour from
 * the health engine cannot disagree about "late".
 *
 * WHICH STEPS, IN WHICH ORDER AND WITH WHICH WORDS IS THE CONFIGURATION'S
 * (`FulfillmentConfiguration.onboardingSteps`; nine in OFMOS, register D5), resolved by api
 * name. A step whose field does not exist in this installation is ABSENT (not open, not done),
 * and the count reads "of N" with N the steps that exist.
 */

/** This build's step field names, in the configured order. Labels are the fields' own labels. */
export const ONBOARDING_STEP_NAMES: readonly string[] = FULFILLMENT.onboardingSteps.map(
  (step) => step.field,
);

/** A field, not a step: one small line in the checklist header. */
export const WELCOME_FIELD_NAME = 'welcome_sent_at';

/** `cohort_enrollment.phase` options that put an enrollment on the board, by key and label. */
export const BOARD_PHASES = [
  { key: 'pre_onboarding', label: 'Pre-onboarding' },
  { key: 'activation', label: 'Activation' },
] as const;

/** A contract that started within this many days is on the board whatever its phase. */
export const BOARD_RECENT_DAYS = 14;

/**
 * WHAT A STEP IS FOR: the configured step's `purpose`, where it has one. The TIMING half is
 * never configured: it is generated from the field's checkpoint setting by `deadlineRule`, so
 * the sentence cannot say "48 hours" while the setting says 2 days, and an operator who changes
 * an offset changes the sentence. In OFMOS "Intake filled" has no purpose sentence any more,
 * only its deadline rule (redesign register A1, question Q-ON-7 answered A; ledger RD2-CALLS-1).
 */
function stepPurpose(fieldName: string, config: FulfillmentConfiguration): string | undefined {
  return config.onboardingSteps.find((step) => step.field === fieldName)?.purpose;
}

/** How a base date reads inside "Due N days after ..." and "Due on ...". */
const BASE_PHRASES: Readonly<Record<string, { readonly after: string; readonly on: string }>> = {
  contract_start_date: { after: 'joining', on: 'the day of joining' },
  form_submitted_at: { after: 'the intake is filled', on: 'the day the intake is filled' },
  gameplan_written_at: {
    after: 'the gameplan is written',
    on: 'the day the gameplan is written',
  },
  onboarding_call_at: { after: 'the onboarding call', on: 'the day of the onboarding call' },
};

/**
 * WHEN A STEP IS DUE, as the deadline list under the Onboarding legend prints it beside the
 * step's name: "2 days after joining", "on the day of the onboarding call" (register A6-5, A7-R2;
 * the redraw onboarding-v3 drops the "Due" that its heading "Deadlines" already says). Null
 * without a checkpoint setting or a base date.
 */
export function deadlineWhen(
  settings: CheckpointSettings | undefined,
  fieldsById: ReadonlyMap<string, FieldMetadata>,
): string | null {
  if (settings === undefined) return null;
  const baseId = settings.baseFieldId ?? settings.blockedByFieldId;
  if (baseId === undefined) return null;
  const base = fieldsById.get(baseId);
  if (base === undefined) return null;
  const phrase = BASE_PHRASES[base.name] ?? {
    after: base.label.toLowerCase(),
    on: `the day of ${base.label.toLowerCase()}`,
  };
  const days = settings.offsetDays;
  if (days === 0) return `on ${phrase.on}`;
  return `${days} ${Math.abs(days) === 1 ? 'day' : 'days'} after ${phrase.after}`;
}

/** "Due 2 days after joining." from the checkpoint setting, or null without one. */
export function deadlineRule(
  settings: CheckpointSettings | undefined,
  fieldsById: ReadonlyMap<string, FieldMetadata>,
): string | null {
  const when = deadlineWhen(settings, fieldsById);
  return when === null ? null : `Due ${when}.`;
}

/** The one-line rule under a step's label: what it is for, then when it is due. */
export function stepRule(
  field: FieldMetadata,
  fieldsById: ReadonlyMap<string, FieldMetadata>,
  config: FulfillmentConfiguration = FULFILLMENT,
): string {
  const parts = [
    stepPurpose(field.name, config),
    deadlineRule(field.options?.checkpoint, fieldsById),
  ].filter((part): part is string => part !== undefined && part !== null);
  return parts.join(' ');
}

/** The steps this installation has, in the configured order. Absent names are simply not there. */
export function onboardingSteps(
  fields: readonly FieldMetadata[],
  config: FulfillmentConfiguration = FULFILLMENT,
): readonly FieldMetadata[] {
  const byName = new Map(fields.map((field) => [field.name, field]));
  return config.onboardingSteps.flatMap(({ field: name }) => {
    const field = byName.get(name);
    return field !== undefined && field.type === 'DATE' ? [field] : [];
  });
}

export type Values = Readonly<Record<string, FieldValue>>;

export function stepStates(
  steps: readonly FieldMetadata[],
  values: Values,
  today: string,
): readonly CheckpointState[] {
  return steps.map((field) => checkpointState(toCheckpointField(field), values, today));
}

/**
 * "3 of 6 done": done over the steps that exist and are still owed. A WAIVED step (finding
 * F6) is not owed, so it counts in neither number, and a client whose remaining steps are all
 * done leaves the board although the three steps before the call never got a date.
 */
export function progressOf(states: readonly CheckpointState[]): {
  readonly done: number;
  readonly total: number;
} {
  return {
    done: states.filter((state) => state.status === 'done').length,
    total: states.filter((state) => state.status !== 'waived').length,
  };
}

/**
 * THE NEXT STEP AND ITS SENTENCE. `nextOpenCheckpoint` decides (earliest deadline among the
 * open, unblocked steps, the configured order on ties). When every open step is blocked it
 * answers nothing, and the row still owes something: then the first open step in that order is
 * named with "Not yet possible", which is the state that step is in.
 */
export interface NextStep {
  /** The step's field id: the Board puts the client in this step's column. */
  readonly fieldId: string;
  readonly label: string;
  readonly sentence: string | null;
  readonly status: CheckpointStatus;
  /** How many days the step is late, 0 when it is not. */
  readonly lateDays: number;
}

function lateDaysOf(state: CheckpointState): number {
  return state.status === 'late' && state.days !== null ? -state.days : 0;
}

export function nextStepOf(
  steps: readonly FieldMetadata[],
  values: Values,
  today: string,
): NextStep | null {
  const checkpointFields = steps.map(toCheckpointField);
  const next = nextOpenCheckpoint(values, checkpointFields, today);
  if (next !== null) {
    return {
      fieldId: next.field.id,
      label: next.field.label,
      sentence: deadlineSentence(next.state),
      status: next.state.status,
      lateDays: lateDaysOf(next.state),
    };
  }
  for (const field of checkpointFields) {
    const state = checkpointState(field, values, today);
    if (state.status !== 'done' && state.status !== 'waived') {
      return {
        fieldId: field.id,
        label: field.label,
        sentence: deadlineSentence(state),
        status: state.status,
        lateDays: lateDaysOf(state),
      };
    }
  }
  return null;
}

/** The label of the last ticked step in the configured order, or "Client joined". */
export function lastTickedLabel(
  steps: readonly FieldMetadata[],
  states: readonly CheckpointState[],
): string {
  for (let index = steps.length - 1; index >= 0; index -= 1) {
    if (states[index]?.status === 'done') return steps[index]?.label ?? 'Client joined';
  }
  return 'Client joined';
}

/**
 * WHOLE CALENDAR DAYS SINCE A DAY, through the shared checklist's own arithmetic.
 *
 * A zero-offset checkpoint counted from `day` has its deadline ON `day`, and the shared state
 * reports the signed days from today to that deadline - which is exactly minus the days since.
 * Using it rather than a second date difference is the brief's rule: one place decides what a
 * calendar day is (DST, month ends), and it is `shared/checklist.ts`.
 */
export function daysSince(day: string, today: string): number | null {
  const probe = {
    id: '__since',
    label: '',
    type: 'DATE' as const,
    settings: { checkpoint: { baseFieldId: '__base', offsetDays: 0 } },
  };
  const state = checkpointState(probe, { __base: day }, today);
  return state.days === null ? null : -state.days;
}

/** "Day 9 · Onboarding call", or "No start date". */
export function dayLine(contractStart: string | null, lastLabel: string, today: string): string {
  const count = dayCount(contractStart, today);
  return count === 'No start date' ? count : `${count} · ${lastLabel}`;
}

/**
 * "Day 9", "Starts in 2 days" or "No start date": the day line WITHOUT its last-step part, as
 * the redesigned page draws it under a client's name (onboarding-v2.html:158, the matrix's
 * client column).
 */
export function dayCount(contractStart: string | null, today: string): string {
  if (contractStart === null) return 'No start date';
  const days = daysSince(contractStart, today);
  if (days === null) return 'No start date';
  if (days < 0) return `Starts in ${-days} ${-days === 1 ? 'day' : 'days'}`;
  return `Day ${days}`;
}

/** The most days any step is late, 0 when none is. */
export function mostLateDays(states: readonly CheckpointState[]): number {
  let most = 0;
  for (const state of states) {
    if (state.status === 'late' && state.days !== null) most = Math.max(most, -state.days);
  }
  return most;
}

export interface BoardCandidate {
  /** The enrollment's phase option LABEL, or null. */
  readonly phaseLabel: string | null;
  readonly contractStart: string | null;
  /** Agency lifecycle "Churned" or enrollment status "Withdrawn" (ledger FX-P4-2). */
  readonly churned: boolean;
  readonly progress: { readonly done: number; readonly total: number };
}

/**
 * WHO IS ON THE BOARD (COMMON section 4): phase Pre-onboarding or Activation, OR a contract
 * start within the last 14 days; never churned; gone once every step that exists has a date.
 */
export function isOnBoard(candidate: BoardCandidate, today: string): boolean {
  if (candidate.churned) return false;
  if (candidate.progress.total > 0 && candidate.progress.done === candidate.progress.total) {
    return false;
  }
  if (BOARD_PHASES.some((entry) => entry.label === candidate.phaseLabel)) return true;
  if (candidate.contractStart === null) return false;
  const since = daysSince(candidate.contractStart, today);
  return since !== null && since >= 0 && since <= BOARD_RECENT_DAYS;
}

export interface SortableRow {
  readonly lateDays: number;
  readonly contractStart: string | null;
  readonly name: string;
}

/**
 * THE ORDER: any late step first, most late first; then the newest contract start first; an
 * enrollment without a start date last. The name breaks remaining ties so the order is stable.
 */
export function compareBoardRows(a: SortableRow, b: SortableRow): number {
  if (a.lateDays !== b.lateDays) return b.lateDays - a.lateDays;
  if (a.contractStart !== b.contractStart) {
    if (a.contractStart === null) return 1;
    if (b.contractStart === null) return -1;
    return a.contractStart < b.contractStart ? 1 : -1;
  }
  return a.name.localeCompare(b.name);
}

/**
 * THE OWNER POPUP'S ORDER: the members who own the most clients on the board first, then by
 * name. It is the owner menu's own order (`shared/ownerMenu.ts`, the coordinator's rule of
 * 4 Oct for the Coach groups and the phone's segments), so the two lists of owners on this page
 * agree, and no person is named in platform code (DESIGN.md Part 1 priority 7, D16). The order
 * comes from the data; a member who owns no board client keeps a place, alphabetically.
 */
export function orderTeam<T extends { readonly id: string; readonly name: string }>(
  team: readonly T[],
  rows: readonly { readonly ownerId: string | null }[],
): readonly T[] {
  const owned = new Map<string, number>();
  for (const row of rows) {
    if (row.ownerId !== null) owned.set(row.ownerId, (owned.get(row.ownerId) ?? 0) + 1);
  }
  return team.toSorted(
    (a, b) => (owned.get(b.id) ?? 0) - (owned.get(a.id) ?? 0) || a.name.localeCompare(b.name),
  );
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

function dayParts(value: unknown) {
  if (typeof value !== 'string') return null;
  const match = value.match(ISO_DAY);
  if (match === null) return null;
  return { year: match[1] ?? '', month: match[2] ?? '', day: match[3] ?? '' };
}

/** `2026-08-22` as `22.08.2026` (the checklist header, Ben's format). */
export function longDay(value: unknown): string | null {
  const parts = dayParts(value);
  return parts === null ? null : `${parts.day}.${parts.month}.${parts.year}`;
}

/** `2026-08-22` as `22.08` (a step's date and the welcome line). */
export function shortDay(value: unknown): string | null {
  const parts = dayParts(value);
  return parts === null ? null : `${parts.day}.${parts.month}`;
}

/** "auto" for any machine writer, the first name for a person, null when unknown. */
export function sourceWord(
  actor: { readonly source: string; readonly displayName?: string | undefined } | undefined,
): string | null {
  if (actor === undefined) return null;
  if (actor.source !== 'MANUAL') return 'auto';
  const first = actor.displayName?.trim().split(/\s+/)[0];
  return first === undefined || first === '' ? null : first;
}

/**
 * THE ORDER HOLDS WHILE THE SAME CLIENTS ARE ON THE SCREEN (ledger FX-P4-5).
 *
 * A tick changes how late a row is, and re-sorting on every tick would move the row the
 * person is working in out from under their pointer - with its checklist open. So the sorted
 * order is adopted only when the SET of rows changes (a client joins or leaves the board, the
 * page is opened again); until then the previous order is kept and a row stays where it was.
 */
export function stableOrder<T extends { readonly id: string }>(
  previous: readonly string[],
  sorted: readonly T[],
): readonly T[] {
  if (previous.length !== sorted.length) return sorted;
  const byId = new Map(sorted.map((row) => [row.id, row]));
  const kept: T[] = [];
  for (const id of previous) {
    const row = byId.get(id);
    if (row === undefined) return sorted;
    kept.push(row);
  }
  return kept;
}
